import type { FinancialAccount } from "@common/types/financialAccount";
import type { CounterpartyDirection } from "@common/helpers/counterparties";
import { FilterTabs } from "../pageHeader/filters/lib";
import { ArAgingBucket } from "@common/types/reports/ar-aging";
import type { DirectionRadioDef } from "../pageHeader/filters/Tabs/AmountFilter/directionRadios";
import { FiltersTypes } from "@common/types/filters/general";
import type { AccountingReportFilters } from "./types";

export interface AgingBucketsPartition {
    agingBuckets: ArAgingBucket[] | undefined;
    restFilters: Partial<FiltersTypes>;
    hasRestChanges: boolean;
}

/**
 * A/R Aging Detail keeps its aging-period selection outside the shared filter
 * tab context (see `AccountingReportFiltersForm`'s `agingBuckets` override
 * props). When the filters overlay emits a change that touches `agingBuckets`
 * — a period toggle, the "Aging period" chip reset, or a "clear all filters"
 * payload — this splits the buckets (routed to the separate state owner) from
 * the remaining filter changes (routed to the normal `setFilters`), so the two
 * owners never drift out of sync.
 */
export function partitionAgingBucketsFromFilters(
    newFilters: Partial<FiltersTypes>,
): AgingBucketsPartition {
    const { agingBuckets, ...restFilters } = newFilters;
    return {
        agingBuckets,
        restFilters,
        hasRestChanges: Object.keys(restFilters).length > 0,
    };
}

export function mapGeneralLedgerFilters(filters: AccountingReportFilters = {}) {
    return {
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        counterpartiesIds: filters.counterpartiesIds,
        withEmptyOrUnverifiedCounterparty:
            filters.withEmptyOrUnverifiedCounterparty,
        hasCounterparty: filters.hasCounterparty,
        accountCodes: filters.accountCodes,
        sourceTypes: filters.journalEntrySources,
        minAmount: filters.minAmount,
        maxAmount: filters.maxAmount,
        direction: filters.direction,
        amountRounded: filters.amountRounded,
        amountRoundingLevel: filters.amountRoundingLevel,
        excludeFrom1099: filters.excludeFrom1099,
        financialAccountIds: filters.accountIds,
        subaccountIds: filters.subaccountIds,
    };
}

export function mapSchedulesReportFilters(
    filters: AccountingReportFilters = {},
) {
    return {
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        templateIds: filters.scheduleTemplateIds,
        groups: filters.scheduleGroups,
        termStartFrom: filters.termStartFrom,
        termStartTo: filters.termStartTo,
        termEndFrom: filters.termEndFrom,
        termEndTo: filters.termEndTo,
        counterpartyIds: filters.counterpartiesIds,
        includeUnassignedCounterparty:
            filters.withEmptyOrUnverifiedCounterparty,
        hasCounterparty: filters.hasCounterparty,
        minAmount: filters.minAmount,
        maxAmount: filters.maxAmount,
    };
}

export function mapArAgingFilters(filters: AccountingReportFilters = {}) {
    return {
        classes: filters.classes,
        classFilterType: filters.classFilterType,
        customerId: filters.customerId,
        termTypes: filters.termTypes,
        accountIds: filters.accountIds,
        invoiceSources: filters.invoiceSources,
        createdOnFrom: filters.createdOnFrom,
        createdOnTo: filters.createdOnTo,
        dueDateFrom: filters.dueDateFrom,
        dueDateTo: filters.dueDateTo,
    };
}

export interface AccountingReportFilterProps {
    accountFilterPredicate?: (account: FinancialAccount) => boolean;
    shouldShowKickAccountOption?: boolean;
    counterpartyEntityLabel?: string;
    counterpartyDirection?: CounterpartyDirection;
    counterpartyInvoicesOnly?: boolean;
    counterpartySingleSelect?: boolean;
    bindCounterpartyFilterToCustomerId?: boolean;
    directionRadios?: DirectionRadioDef[];
    hideClassFilter?: boolean;
    showAccountFilter?: boolean;
    showAccountEnabledFilter?: boolean;
    showAccountSourceFilter?: boolean;
    showAccountRolePresenceFilter?: boolean;
    showAccountRulePresenceFilter?: boolean;
    showForm1099BoxFilter?: boolean;
    showGroupFilter?: boolean;
    showJournalEntrySourceFilter?: boolean;
    showAmountFilter?: boolean;
    showCounterpartyFilter?: boolean;
    showExclude1099Filter?: boolean;
    showSchedulesReportFilters?: boolean;
    showArAgingInvoiceFilters?: boolean;
    showInvoiceAccountFilter?: boolean;
    showFinancialAccountFilter?: boolean;
    showAgingPeriodFilter?: boolean;
}

export function getAccountingReportFilterOverlayProps({
    accountFilterPredicate,
    shouldShowKickAccountOption,
    counterpartyEntityLabel,
    counterpartyDirection,
    counterpartyInvoicesOnly,
    counterpartySingleSelect,
    bindCounterpartyFilterToCustomerId,
    directionRadios,
}: AccountingReportFilterProps = {}) {
    return {
        accountFilterPredicate,
        shouldShowKickAccountOption,
        counterpartyEntityLabel,
        counterpartyDirection,
        counterpartyInvoicesOnly,
        counterpartySingleSelect:
            counterpartySingleSelect || bindCounterpartyFilterToCustomerId,
        directionRadios,
        directionSwitchDisabled: !directionRadios,
    };
}

export function getAccountingReportAvailableFilters({
    hideClassFilter,
    showAccountFilter,
    showAccountEnabledFilter,
    showAccountSourceFilter,
    showAccountRolePresenceFilter,
    showAccountRulePresenceFilter,
    showForm1099BoxFilter,
    showGroupFilter,
    showJournalEntrySourceFilter,
    showAmountFilter,
    showCounterpartyFilter,
    showExclude1099Filter,
    showSchedulesReportFilters,
    showArAgingInvoiceFilters,
    showInvoiceAccountFilter,
    showFinancialAccountFilter,
    showAgingPeriodFilter,
    hasClassesAccess,
}: AccountingReportFilterProps & { hasClassesAccess: boolean }): FilterTabs[] {
    const availableFilters: FilterTabs[] = [];

    if (!hideClassFilter && hasClassesAccess) {
        availableFilters.push(FilterTabs.CLASS);
    }

    if (showAccountFilter) {
        availableFilters.push(FilterTabs.ACCOUNT_CODE);
    }

    if (showAccountEnabledFilter) {
        availableFilters.push(FilterTabs.ACCOUNT_STATUS);
    }

    if (showAccountSourceFilter) {
        availableFilters.push(FilterTabs.ACCOUNT_SOURCE);
    }

    if (showAccountRolePresenceFilter) {
        availableFilters.push(FilterTabs.ACCOUNT_ROLE_PRESENCE);
    }

    if (showAccountRulePresenceFilter) {
        availableFilters.push(FilterTabs.ACCOUNT_RULE_PRESENCE);
    }

    if (showForm1099BoxFilter) {
        availableFilters.push(FilterTabs.FORM_1099_BOX);
    }

    if (showGroupFilter) {
        availableFilters.push(FilterTabs.COA_TEMPLATE_GROUPS);
    }

    if (showJournalEntrySourceFilter) {
        availableFilters.push(FilterTabs.JOURNAL_ENTRY_SOURCES);
    }

    if (showAmountFilter) {
        availableFilters.push(FilterTabs.AMOUNT);
    }

    if (showCounterpartyFilter) {
        availableFilters.push(FilterTabs.COUNTERPARTY);
    }
    if (showExclude1099Filter) {
        availableFilters.push(FilterTabs.EXCLUDE_1099);
    }
    if (showSchedulesReportFilters) {
        availableFilters.push(
            FilterTabs.SCHEDULES_REPORT_TEMPLATE,
            FilterTabs.SCHEDULES_REPORT_GROUP,
            FilterTabs.HOLDING_ACCOUNT,
            FilterTabs.TERM_START,
            FilterTabs.TERM_END,
        );
    }
    if (showArAgingInvoiceFilters) {
        availableFilters.push(
            FilterTabs.INVOICE_TERMS,
            FilterTabs.INVOICE_CREATED_ON,
            FilterTabs.INVOICE_DUE_DATE,
        );
    }
    if (showInvoiceAccountFilter || showFinancialAccountFilter) {
        availableFilters.push(FilterTabs.ACCOUNT);
    }
    if (showAgingPeriodFilter) {
        availableFilters.push(FilterTabs.AGING_PERIOD);
    }

    return availableFilters;
}
